// SurveyJS fetches `choicesByUrl` option lists with its own XMLHttpRequest,
// which omits cookies by default, so any endpoint behind auth answers 401.
// Opting those requests into credentials lets forms pull option lists from our
// own APIs — but only from ours: a `choicesByUrl` is author-supplied, and an
// arbitrary third-party host that replies with
// `Access-Control-Allow-Credentials: true` would otherwise receive the
// session cookie.
import { settings as coreSettings } from "survey-core";

import { API_REGIONS, baseUrlForRegion } from "@/api/region";
import {
  APP_ALERT_SERVER_URL,
  APP_CAMERA_SERVER_URL,
  APP_OR_SERVER_URL,
} from "@/environment";

type BeforeRequestChoices = typeof coreSettings.web.onBeforeRequestChoices;

const originOf = (url: string): string | null => {
  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return null;
  }
};

/**
 * Origins the session cookie may travel to: the portal itself, the Python API,
 * the camera service, and the Rust API in every region (`setApiRegion` can
 * re-point it at either host mid-session).
 */
const credentialedOrigins = (): Set<string> => {
  const urls = [
    window.location.origin,
    APP_ALERT_SERVER_URL,
    APP_CAMERA_SERVER_URL,
    ...(APP_OR_SERVER_URL
      ? API_REGIONS.map((region) => baseUrlForRegion(APP_OR_SERVER_URL, region))
      : []),
  ];

  return new Set(
    urls
      .filter((url): url is string => !!url)
      .map(originOf)
      .filter((origin): origin is string => origin !== null),
  );
};

export const configureSurveyChoicesAuth = () => {
  const allowed = credentialedOrigins();

  const onBeforeRequestChoices: BeforeRequestChoices = (_sender, options) => {
    const origin = originOf(options.url);
    if (!origin || !allowed.has(origin)) return;

    if (options.request) {
      options.request.withCredentials = true;
    }
    if (options.fetchOptions) {
      options.fetchOptions.credentials = "include";
    }
  };

  coreSettings.web.onBeforeRequestChoices = onBeforeRequestChoices;
};
